/**
 * @page /kunden-rotation/tv
 * @description Rotation TV-Ansicht — Fullscreen 16:9 Schichtplan-Display
 *   fuer Lager-Monitore mit Echtzeit-Uhr und Auto-Refresh.
 * @features
 *   - Vollbild-Darstellung optimiert fuer TV/Monitor
 *   - Echtzeit-Uhr mit Sekunden-Takt
 *   - Schichtmodell-Auswahl via URL-Parameter
 *   - Slot-Anzeige mit Kunden-Farbkodierung
 *   - Pausen-Konfiguration und Ring-Diagramm
 *   - Auto-Refresh und Flash-Animation bei Aenderungen
 * @api GET /api/schichtmodelle
 * @api GET /api/rotation?datum=YYYY-MM-DD&schicht_id=X
 * @api GET /api/pausen-config?schicht_id=X
 * @roles admin, manager
 * @i18n rotation
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Ring from '../components/Ring';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ─────────────────────────────────────────────────────

function api(path) {
  return fetch(path, { headers: { 'Content-Type': 'application/json' } }).then(r => r.json());
}

const kundeColors = [
  '#ef4444', '#338edc', '#f59e0b', '#4D9CE0', '#06b6d4',
  '#ec4899', '#34d399', '#f97316', '#6366f1', '#14b8a6',
  '#e879f9', '#fb923c', '#22d3ee', '#a3e635', '#f472b6',
];
function getKundeColor(idx) { return kundeColors[idx % kundeColors.length]; }

const dayLabels = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function parseTime(t) {
  if (!t) return 0;
  const s = typeof t === 'string' ? t : String(t);
  const [h, m] = s.split(':').map(Number);
  return h * 60 + (m || 0);
}
function fmtTime(min) {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}
function fmtTimeStr(tv) { return tv ? tv.slice(0, 5) : ''; }

function ratioColor(r) {
  if (r >= 1.0) return '#34d399';
  if (r >= 0.8) return '#eab308';
  return '#ef4444';
}

// ═══════════════════════════════════════════════════════════════════
// TV BOARD — Fullscreen 16:9 optimiert
// ═══════════════════════════════════════════════════════════════════

const dayLabelsShort = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export default function RotationTV() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const schichtParam = searchParams.get('schicht');
  const datumParam = searchParams.get('datum');

  const [schichtmodelle, setSchichtmodelle] = useState([]);
  const [schichtId, setSchichtId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [pausen, setPausen] = useState([]);
  const [clock, setClock] = useState(new Date());
  const [flashId, setFlashId] = useState(null);
  const prevIstRef = useRef({});

  // Datum is derived from URL (single source of truth)
  const datum = datumParam || new Date().toISOString().slice(0, 10);

  // ── Clock: tick every second ──
  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // ── ESC → back ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        document.exitFullscreen?.().catch(() => {});
        navigate('/kunden-rotation');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // ── Fullscreen API ──
  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    }
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  // ── Date navigation (updates URL → triggers data reload) ──
  const setDatum = (newDatum) => {
    prevIstRef.current = {};
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('datum', newDatum);
      return p;
    }, { replace: true });
  };
  const prevDay = () => {
    const d = new Date(datum + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    setDatum(d.toISOString().slice(0, 10));
  };
  const nextDay = () => {
    const d = new Date(datum + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    setDatum(d.toISOString().slice(0, 10));
  };
  const goToday = () => setDatum(new Date().toISOString().slice(0, 10));

  // ── Shift change ──
  const switchSchicht = (id) => {
    setSchichtId(id);
    prevIstRef.current = {};
  };

  // ── Load Schichtmodelle ──
  useEffect(() => {
    api('/api/schichtmodelle').then(sm => {
      const list = Array.isArray(sm) ? sm : [];
      setSchichtmodelle(list);
      if (list.length === 0) return;
      if (!schichtId) {
        if (schichtParam) {
          const match = list.find(s => {
            const n = s.name.toLowerCase();
            if (schichtParam === 'frueh') return n.includes('früh') || n.includes('fruh');
            if (schichtParam === 'spaet') return n.includes('spät') || n.includes('spat') || n.includes('nach');
            return false;
          });
          setSchichtId(match ? match.id : list[0].id);
        } else {
          setSchichtId(list[0].id);
        }
      }
    });
  }, [schichtParam]);

  // ── Load rotation data ──
  const loadData = useCallback(async () => {
    if (!schichtId) return;
    const [sl, pc] = await Promise.all([
      api(`/api/rotation?datum=${datum}&schicht_id=${schichtId}`),
      api(`/api/pausen-config?schicht_id=${schichtId}`),
    ]);
    const newSlots = Array.isArray(sl) ? sl : [];

    // Green flash: IST just exceeded SOLL
    newSlots.forEach(s => {
      if (!s.ist_pause && s.ist_pakete != null && s.soll_pakete > 0) {
        const prev = prevIstRef.current[s.id];
        if (prev !== undefined && prev < s.soll_pakete && s.ist_pakete >= s.soll_pakete) {
          setFlashId(s.id);
          setTimeout(() => setFlashId(null), 2000);
        }
        prevIstRef.current[s.id] = s.ist_pakete;
      }
    });

    setSlots(newSlots);
    setPausen(Array.isArray(pc) ? pc : []);
  }, [datum, schichtId]);

  // ── Auto-refresh 30s ──
  useEffect(() => {
    loadData();
    const iv = setInterval(loadData, 30000);
    return () => clearInterval(iv);
  }, [loadData]);

  // ─── Computed ──────────────────────────────────────────────────

  const currentSm = useMemo(() =>
    schichtmodelle.find(s => s.id === schichtId) || null,
    [schichtmodelle, schichtId]);

  const shiftStart = currentSm ? parseTime(currentSm.start_zeit) : 360;
  const shiftEnd = currentSm ? parseTime(currentSm.end_zeit) : 840;
  const shiftDuration = shiftEnd - shiftStart;

  const pickers = useMemo(() => {
    const map = new Map();
    slots.forEach(s => {
      if (!map.has(s.mitarbeiter_id))
        map.set(s.mitarbeiter_id, { id: s.mitarbeiter_id, name: s.ma_name, kuerzel: s.ma_kuerzel });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [slots]);

  const kundeColorMap = useMemo(() => {
    const map = {};
    [...new Set(slots.filter(s => !s.ist_pause && s.kunde_id).map(s => s.kunde_id))].forEach((kid, i) => {
      map[kid] = getKundeColor(i);
    });
    map['mixed'] = '#6b7280'; // neutral gray for mixed-wave
    return map;
  }, [slots]);

  const timeSlots = useMemo(() => {
    const res = [];
    for (let tv = shiftStart; tv < shiftEnd; tv += 60) res.push(tv);
    return res;
  }, [shiftStart, shiftEnd]);

  const nowMin = clock.getHours() * 60 + clock.getMinutes();
  const isToday = datum === clock.toISOString().slice(0, 10);

  const nowPct = useMemo(() => {
    if (!isToday) return null;
    if (nowMin < shiftStart || nowMin > shiftEnd) return null;
    return ((nowMin - shiftStart) / shiftDuration) * 100;
  }, [isToday, nowMin, shiftStart, shiftEnd, shiftDuration]);

  // Next pause countdown
  const nextPauseInfo = useMemo(() => {
    const pauseSlots = slots.filter(s => s.ist_pause);
    if (pauseSlots.length === 0) return null;
    const upcoming = pauseSlots
      .filter(s => parseTime(s.end_zeit) > nowMin)
      .sort((a, b) => parseTime(a.start_zeit) - parseTime(b.start_zeit));
    if (upcoming.length === 0) return null;
    const next = upcoming[0];
    const startMin = parseTime(next.start_zeit);
    if (nowMin >= startMin) return { label: t('rotation.breakRunning'), minutes: parseTime(next.end_zeit) - nowMin, active: true };
    return { label: t('rotation.nextBreak'), minutes: startMin - nowMin, active: false };
  }, [slots, nowMin, t]);

  // Per-picker stats for status bar
  const pickerStats = useMemo(() => {
    return pickers.map(p => {
      const ps = slots.filter(s => s.mitarbeiter_id === p.id && !s.ist_pause);
      const totalSoll = ps.reduce((sum, s) => sum + (Number(s.soll_pakete) || 0), 0);
      const totalIst = ps.reduce((sum, s) => sum + (Number(s.ist_pakete) || 0), 0);
      const ratio = totalSoll > 0 ? totalIst / totalSoll : null;
      const activeSlot = isToday && ps.find(s => {
        const st = parseTime(s.start_zeit);
        const en = parseTime(s.end_zeit);
        return nowMin >= st && nowMin < en;
      });
      return { ...p, totalSoll, totalIst, ratio, activeSlot, slotCount: ps.length };
    });
  }, [pickers, slots, nowMin, isToday]);

  // Totals
  const totals = useMemo(() => {
    const work = slots.filter(s => !s.ist_pause);
    const soll = work.reduce((sum, s) => sum + (Number(s.soll_pakete) || 0), 0);
    const ist = work.reduce((sum, s) => sum + (Number(s.ist_pakete) || 0), 0);
    const ratio = soll > 0 ? ist / soll : null;
    return { soll, ist, ratio };
  }, [slots]);

  // Date display
  const todayStr = new Date().toISOString().slice(0, 10);
  const dateObj = new Date(datum + 'T12:00:00');
  const dateDisplayShort = `${dayLabelsShort[dateObj.getDay()]}, ${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;

  // Clock HH:MM:SS
  const clockStr = [clock.getHours(), clock.getMinutes(), clock.getSeconds()]
    .map(n => String(n).padStart(2, '0')).join(':');

  const goBack = () => {
    document.exitFullscreen?.().catch(() => {});
    navigate('/kunden-rotation');
  };

  // Dynamic lane height: fit all pickers on screen
  const HEADER_H = 72, FOOTER_H = 76, AXIS_H = 36, PAD = 24;
  const available = typeof window !== 'undefined' ? window.innerHeight - HEADER_H - FOOTER_H - AXIS_H - PAD : 800;
  const laneH = pickers.length > 0 ? Math.max(64, Math.min(96, Math.floor((available - pickers.length * 4) / pickers.length))) : 80;

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="tv-mode fixed inset-0 bg-black text-white flex flex-col overflow-hidden select-none">

      {/* ── HEADER ────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-8 border-b border-white/5 flex-shrink-0 bg-gray-950/80"
        style={{ height: HEADER_H }}>

        {/* Logo — click → back */}
        <div className="flex items-center gap-3 cursor-pointer group" onClick={goBack}
          title={t('rotation.backToRotation')}>
          <img src="/weship-logo-light.png" alt="WeShip" className="h-8 group-hover:scale-105 transition-transform" />
        </div>

        {/* ── Date Navigation ── */}
        <div className="flex items-center gap-2">
          <button onClick={prevDay}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-[var(--text-tertiary,#6B7280)] hover:text-white transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <span className="font-medium text-gray-100 min-w-[200px] text-center" style={{ fontSize: 20 }}>
            {dateDisplayShort}
          </span>
          <button onClick={nextDay}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-[var(--text-tertiary,#6B7280)] hover:text-white transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
          {datum !== todayStr && (
            <button onClick={goToday}
              className="px-3 py-1 rounded-lg text-xs font-medium bg-white/5 border border-white/10 text-[var(--text-tertiary,#6B7280)] hover:text-white hover:bg-white/10 transition-colors ml-1">
              {t('rotation.today')}
            </button>
          )}
        </div>

        {/* Digital Clock */}
        <div className="font-mono font-bold text-white leading-none tracking-wider"
          style={{ fontSize: 48 }}>
          {clockStr}
        </div>

        {/* ── Shift Toggle + Pause ── */}
        <div className="flex items-center gap-3">
          {/* Schicht Toggle */}
          {schichtmodelle.length > 1 && (
            <div className="flex bg-white/5 rounded-card p-0.5 border border-white/10">
              {schichtmodelle.map(sm => (
                <button key={sm.id} onClick={() => switchSchicht(sm.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                    schichtId === sm.id
                      ? 'bg-white/10 text-white'
                      : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-tertiary,#6B7280)]'
                  }`}>
                  {sm.name}
                </button>
              ))}
            </div>
          )}
          {schichtmodelle.length <= 1 && currentSm && (
            <div className="text-xs text-[var(--text-secondary,#4B5563)]">
              {currentSm.name} · {fmtTimeStr(currentSm.start_zeit)}–{fmtTimeStr(currentSm.end_zeit)}
            </div>
          )}

          {/* Pause countdown */}
          {nextPauseInfo && (
            <div className="bg-white/[0.04] border border-white/10 rounded-card px-3 py-1.5 text-center min-w-[90px]">
              <div className="text-[8px] text-[var(--text-secondary,#4B5563)] uppercase tracking-widest">{nextPauseInfo.label}</div>
              <div className={`font-mono font-bold leading-tight ${nextPauseInfo.active ? 'text-amber-400' : 'text-white'}`}
                style={{ fontSize: 16 }}>
                {nextPauseInfo.minutes} min
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ── TIMELINE ──────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto px-8 pt-3 pb-2">
        {slots.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[var(--text-secondary,#4B5563)]">
            <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.2" className="mb-5 text-[var(--text-primary,#1A1A2E)]">
              <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
            </svg>
            <div className="text-2xl font-medium text-[var(--text-secondary,#4B5563)]">{t('rotation.noRotationFor')} {dateDisplayShort}</div>
            <div className="text-sm mt-2 text-[var(--text-secondary,#4B5563)]">{t('rotation.generateInNormalView')}</div>
          </div>
        ) : (
          <div>
            {/* ─ Time axis ─ */}
            <div className="flex mb-1">
              <div style={{ width: 180 }} className="flex-shrink-0" />
              <div className="flex-1 relative" style={{ height: AXIS_H }}>
                <div className="flex h-full items-end">
                  {timeSlots.map(tv => (
                    <div key={tv} className="flex-1 font-mono text-[var(--text-secondary,#4B5563)] text-left pl-2 border-l border-white/8 pb-1"
                      style={{ fontSize: 14 }}>
                      {fmtTime(tv)}
                    </div>
                  ))}
                </div>
                {nowPct !== null && (
                  <div className="absolute top-0 z-30" style={{ left: `${nowPct}%`, transform: 'translateX(-50%)' }}>
                    <span className="text-[10px] font-bold text-red-400 bg-black/90 px-1.5 py-0.5 rounded
                      border border-red-500/30">{t('rotation.now')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ─ Swimlanes ─ */}
            {pickers.map(picker => {
              const pickerSlots = slots
                .filter(s => s.mitarbeiter_id === picker.id)
                .sort((a, b) => parseTime(a.start_zeit) - parseTime(b.start_zeit));

              return (
                <div key={picker.id} className="flex mb-1.5">
                  {/* Picker name */}
                  <div style={{ width: 180 }} className="flex-shrink-0 flex items-center gap-3 pr-3">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center font-bold
                      bg-primary-500/20 text-primary-400 flex-shrink-0" style={{ fontSize: 14 }}>
                      {picker.kuerzel}
                    </span>
                    <span className="font-semibold text-gray-200 truncate" style={{ fontSize: 15 }}
                      title={picker.name}>
                      {picker.name}
                    </span>
                  </div>

                  {/* Timeline bar */}
                  <div className="flex-1 relative bg-white/[0.02] border border-white/5 rounded-card overflow-hidden"
                    style={{ height: laneH }}>

                    {/* Grid lines */}
                    {timeSlots.map(tv => (
                      <div key={tv} className="absolute top-0 bottom-0 border-l border-white/5"
                        style={{ left: `${((tv - shiftStart) / shiftDuration) * 100}%` }} />
                    ))}

                    {/* NOW line (thick, pulsing) */}
                    {nowPct !== null && (
                      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${nowPct}%` }}>
                        <div className="h-full -ml-[1.5px]" style={{
                          width: 3,
                          backgroundColor: '#ef4444',
                          boxShadow: '0 0 10px rgba(239,68,68,0.6), 0 0 20px rgba(239,68,68,0.3)',
                          animation: 'tvNowPulse 2s ease-in-out infinite',
                        }} />
                      </div>
                    )}

                    {/* Slots */}
                    {pickerSlots.map(slot => {
                      const start = parseTime(slot.start_zeit);
                      const end = parseTime(slot.end_zeit);
                      const left = ((start - shiftStart) / shiftDuration) * 100;
                      const width = ((end - start) / shiftDuration) * 100;
                      const isMixed = slot.slot_typ === 'mixed';
                      const color = isMixed ? kundeColorMap['mixed'] : (kundeColorMap[slot.kunde_id] || '#6b7280');
                      const ratio = slot.soll_pakete > 0 && slot.ist_pakete != null
                        ? slot.ist_pakete / slot.soll_pakete : null;
                      const isCompleted = isToday && nowMin >= end;
                      const isActive = isToday && nowMin >= start && nowMin < end;
                      const isFlashing = flashId === slot.id;

                      // ─ Pause slot ─
                      if (slot.ist_pause) {
                        return (
                          <div key={slot.id}
                            className="absolute top-2 bottom-2 rounded-lg flex items-center justify-center overflow-hidden"
                            style={{ left: `${left}%`, width: `${width}%`, opacity: isCompleted ? 0.35 : 1 }}>
                            <div className="absolute inset-0 bg-gray-500/8" />
                            <div className="absolute inset-0" style={{
                              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.03) 5px, rgba(255,255,255,0.03) 10px)',
                            }} />
                            <span className="relative z-10" style={{ fontSize: 20 }}>☕</span>
                          </div>
                        );
                      }

                      // ─ Work slot ─
                      return (
                        <div key={slot.id}
                          className={`absolute top-2 bottom-2 rounded-lg overflow-hidden transition-all
                            ${isActive ? 'ring-2 ring-white/20' : ''}`}
                          style={{
                            left: `${left}%`,
                            width: `${width}%`,
                            backgroundColor: color + (isCompleted ? '12' : '22'),
                            borderLeft: `4px solid ${color}`,
                            opacity: isCompleted ? 0.45 : 1,
                            animation: isFlashing ? 'tvFlash 0.5s ease-in-out 4' : undefined,
                          }}>
                          <div className="px-2.5 py-1 h-full flex flex-col justify-center min-w-0">
                            {/* Kunde name or Mixed-Wave label */}
                            <div className="font-bold truncate leading-tight" style={{ color, fontSize: isMixed ? 14 : 16 }}>
                              {isMixed ? 'MIXED-WAVE' : slot.kunde_name}
                            </div>
                            {isMixed && (
                              <div className="text-[var(--text-secondary,#4B5563)] truncate" style={{ fontSize: 10 }}>
                                C-Kunden Pool {slot.auftraege_anzahl ? `· ~${slot.auftraege_anzahl} Auftr.` : ''}
                              </div>
                            )}
                            {/* SOLL / IST */}
                            <div className="flex items-baseline gap-2 mt-0.5">
                              <span className="font-mono text-[var(--text-tertiary,#6B7280)]" style={{ fontSize: 12 }}>
                                SOLL {slot.soll_pakete}
                              </span>
                              {slot.ist_pakete != null && (
                                <span className="font-mono font-bold" style={{
                                  fontSize: 22,
                                  lineHeight: 1,
                                  color: ratio !== null ? ratioColor(ratio) : '#6b7280',
                                }}>
                                  {slot.ist_pakete}
                                </span>
                              )}
                            </div>
                            {/* Progress bar */}
                            {ratio !== null && (
                              <div className="rounded-full overflow-hidden mt-1" style={{ height: 6, backgroundColor: 'rgba(255,255,255,0.06)' }}>
                                <div className="h-full rounded-full transition-all duration-500" style={{
                                  width: `${Math.min(ratio * 100, 100)}%`,
                                  backgroundColor: ratioColor(ratio),
                                }} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ── STATUS BAR ────────────────────────────────────────── */}
      {pickerStats.length > 0 && (
        <footer className="flex-shrink-0 border-t border-white/5 bg-gray-950/80 px-8 py-2"
          style={{ height: FOOTER_H }}>
          <div className="flex items-center gap-2.5 h-full overflow-x-auto">

            {/* Per picker */}
            {pickerStats.map(p => (
              <div key={p.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-card border flex-shrink-0
                ${p.activeSlot ? 'bg-white/[0.05] border-white/10' : 'bg-white/[0.02] border-white/5'}`}>
                <Ring pct={p.ratio !== null ? Math.min(p.ratio * 100, 100) : 0}
                  color={p.ratio !== null ? ratioColor(p.ratio) : '#374151'}
                  size={48} stroke={3}>
                  <span className="font-mono font-bold" style={{
                    fontSize: 11,
                    color: p.ratio !== null ? ratioColor(p.ratio) : '#4b5563',
                  }}>
                    {p.ratio !== null ? `${Math.round(p.ratio * 100)}%` : '–'}
                  </span>
                </Ring>
                <div>
                  <div className="font-bold text-gray-200" style={{ fontSize: 13 }}>{p.kuerzel}</div>
                  <div className="font-mono text-[var(--text-secondary,#4B5563)]" style={{ fontSize: 10 }}>
                    {p.totalIst}/{p.totalSoll}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex-1" />

            {/* Totals */}
            <div className="flex items-center gap-5 bg-white/[0.04] border border-white/10 rounded-card px-5 py-2 flex-shrink-0">
              <div className="text-center">
                <div className="text-[8px] text-[var(--text-secondary,#4B5563)] uppercase tracking-widest">{t('rotation.picker')}</div>
                <div className="font-mono font-bold text-xl text-white">{pickers.length}</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-[8px] text-[var(--text-secondary,#4B5563)] uppercase tracking-widest">SOLL</div>
                <div className="font-mono font-bold text-xl text-primary-400">{totals.soll}</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-[8px] text-[var(--text-secondary,#4B5563)] uppercase tracking-widest">IST</div>
                <div className="font-mono font-bold text-xl" style={{
                  color: totals.ratio !== null ? ratioColor(totals.ratio) : '#4b5563',
                }}>{totals.ist || '–'}</div>
              </div>
              {totals.ratio !== null && (
                <>
                  <div className="w-px h-8 bg-white/10" />
                  <Ring pct={Math.min(totals.ratio * 100, 100)} color={ratioColor(totals.ratio)}
                    size={48} stroke={3}>
                    <span className="font-mono font-bold" style={{
                      fontSize: 12, color: ratioColor(totals.ratio),
                    }}>
                      {Math.round(totals.ratio * 100)}%
                    </span>
                  </Ring>
                </>
              )}
            </div>
          </div>
        </footer>
      )}

      {/* ── CSS Animations ── */}
      <style>{`
        @keyframes tvFlash {
          0%, 100% { box-shadow: none; }
          50% { box-shadow: 0 0 30px rgba(52,211,153,0.6), inset 0 0 20px rgba(52,211,153,0.15); }
        }
        @keyframes tvNowPulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 10px rgba(239,68,68,0.6), 0 0 20px rgba(239,68,68,0.3); }
          50% { opacity: 0.6; box-shadow: 0 0 4px rgba(239,68,68,0.3); }
        }
      `}</style>
    </div>
  );
}
